/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getEmailFileUrl } from "./get-email-file-url";
import { useToast } from "@/hooks/use-toast";
import { DownloadIcon, FileIcon, FileImageIcon, FileTextIcon } from "lucide-react";

const formatFileSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (contentType?: string) => {
  if (contentType?.startsWith("image/")) return FileImageIcon;
  if (contentType === "application/pdf") return FileTextIcon;
  return FileIcon;
};

export const EmailInboxAttachment = ({ attachment }: { attachment: any }) => {
  const [downloading, setDownloading] = useState(false);
  const { toast } = useToast();
  const Icon = getFileIcon(attachment?.content_type);

  const onDownload = async () => {
    if (!attachment?.path) return;
    setDownloading(true);
    try {
      const { url, error, message } = await getEmailFileUrl(attachment.path);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast({
          title: "Error",
          variant: "destructive",
          description: error?.status?.message || message || "Unable to fetch attachment link",
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium" title={attachment?.filename}>
            {attachment?.filename}
          </p>
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatFileSize(attachment?.size)}</span>
            <Badge variant={"outline"} className="capitalize">
              {attachment?.source}
            </Badge>
          </p>
        </div>
      </div>
      <Button
        variant={"outline"}
        size={"icon"}
        onClick={onDownload}
        loading={downloading}
        title="Download"
      >
        <DownloadIcon size={16} />
      </Button>
    </div>
  );
};
